import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, ListGroup, Spinner, InputGroup, Button } from 'react-bootstrap';

const ListIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z" />
    </svg>
);

const SearchableDropdown = ({
    apiCall,        // Function to call API (e.g., fetchUnassignedDevices)
    placeholder,
    value,          // Current selected value (e.g., the IMEI string)
    onSelect,       // Callback when user selects an item
    labelKey,       // Key to display (e.g., 'imei')
    valueKey,       // Key to save (e.g., 'imei')
    isInvalid,
    errorMessage,
    // --- New Props for "Add New" functionality ---
    allowNew = false,
    newItemRegex = null, // RegExp to validate the new item (e.g., /^\d{15}$/)
    newItemError = "Invalid format",
    numericOnly = false, // <-- NEW PROP: Restrict input to numbers only
    entityId = null, // <-- NEW PROP: Entity ID to pass to API call
    required = false
}) => {
    // Mode State: 'search' or 'manual'
    const [isManualMode, setIsManualMode] = useState(false);

    const [searchTerm, setSearchTerm] = useState(value || '');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false); // Track if there are more pages

    const wrapperRef = useRef(null);
    const debounceTimer = useRef(null);
    const nativePattern = newItemRegex ? newItemRegex.source : undefined;

    // Sync internal state if parent updates value (only if not typing in manual mode)
    useEffect(() => {
        if (!isManualMode) {
            setSearchTerm(value || '');
        }
    }, [value, isManualMode]);

    // Reset options when entityId changes
    useEffect(() => {
        setOptions([]);
        setPage(1);
        setHasNext(false);
    }, [entityId]);

    // Handle outside click to close dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    // --- API Fetch Logic ---
    const loadItems = useCallback(async (isNewSearch = false, query = searchTerm, currentPage = 1) => {
        setLoading(true);

        try {
            const limit = 10;
            const params = {
                search: query,
                page: currentPage,
                limit: limit
            };

            // Pass entityId to apiCall if provided
            const response = entityId ? await apiCall(params, entityId) : await apiCall(params);

            if (response.data && response.data.success) {
                // Handle different API response structures
                const dataObj = response.data.data;
                const newItems = dataObj?.items || (Array.isArray(dataObj) ? dataObj : []) || [];

                setOptions(newItems);

                // Dynamic Pagination Logic
                if (dataObj?.pagination) {
                    // Use explicit API metadata if available
                    setHasNext(dataObj.pagination.hasNext);
                } else {
                    // Fallback heuristic: If we got a full page, assume there might be more
                    setHasNext(newItems.length === limit);
                }
            }
        } catch (error) {
            console.error("Dropdown fetch error:", error);
            if (isNewSearch) setOptions([]);
            setHasNext(false);
        } finally {
            setLoading(false);
        }
    }, [apiCall, entityId]);

    // --- Pagination Handlers ---
    const handleNextPage = (e) => {
        e.stopPropagation();
        if (hasNext) {
            const nextPage = page + 1;
            setPage(nextPage);
            loadItems(false, searchTerm, nextPage);
        }
    };

    const handlePrevPage = (e) => {
        e.stopPropagation();
        if (page > 1) {
            const prevPage = page - 1;
            setPage(prevPage);
            loadItems(false, searchTerm, prevPage);
        }
    };

    // --- Input Change (Search Mode) ---
    const handleSearchChange = (e) => {
        let val = e.target.value;

        // Apply numeric filter if enabled
        if (numericOnly) {
            val = val.replace(/\D/g, '');
        }
        setSearchTerm(val);
        setShowDropdown(true);

        // Clear parent selection if user types (they are searching now)
        if (val === '') {
            onSelect('');
            setOptions([]);
            setHasNext(false);
            return;
        }

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            setPage(1);
            loadItems(true, val, 1);
        }, 500); // 500ms delay
    };

    // --- Focus Handler ---
    const handleFocus = () => {
        setShowDropdown(true);
        // If options are empty, load initial list
        if (options.length === 0 && searchTerm === '') {
            setPage(1);
            loadItems(true, '', 1);
        }
    };

    const handleSelect = (item) => {
        const displayVal = item[labelKey];
        setSearchTerm(displayVal);
        onSelect(item[valueKey]);
        setShowDropdown(false);
    };

    const handleClear = () => {
        setSearchTerm('');
        onSelect('');
        setOptions([]);
        setPage(1);
        setHasNext(false);
    };

    // --- Manual Mode Handlers ---
    const switchToManual = () => {
        setIsManualMode(true);
        setShowDropdown(false);
        // Pre-fill manual input with whatever was searched
        onSelect(searchTerm);
    };

    const switchToSearch = () => {
        setIsManualMode(false);
        setSearchTerm('');
        onSelect('');
        setOptions([]);
        setPage(1);
        // Optionally trigger loadItems(true, '', 1) if you want to preload list
    };

    const handleManualChange = (e) => {
        let val = e.target.value;

        // Apply numeric filter if enabled
        if (numericOnly) {
            val = val.replace(/\D/g, '');
        }
        setSearchTerm(val); // Keep local state in sync just in case
        onSelect(val); // Update parent directly without API calls
    };

    // --- Validation Check for Manual Entry ---
    const isNewItemValid = () => {
        console.log("newItemRegex...", newItemRegex)
        if (!newItemRegex) return true;
        return newItemRegex.test(searchTerm);
    };

    // --- RENDER ---

    if (isManualMode) {
        return (
            <div ref={wrapperRef}>
                <InputGroup hasValidation>
                    <Form.Control
                        type="text"
                        placeholder={`Enter new ${placeholder.toLowerCase().replace('search', '').trim()}`}
                        value={value || ''}
                        onChange={handleManualChange}
                        isInvalid={isInvalid}
                        required={required}
                        pattern={nativePattern}
                        autoFocus
                        inputMode={numericOnly ? "numeric" : "text"} // Hint for mobile keyboards
                    />
                    <Button variant="outline-secondary" onClick={switchToSearch} title="Return to Search List">
                        {/* <ListIcon /> */}
                        Back to Search
                    </Button>
                    <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>
                </InputGroup>
                {/* <Form.Text className="text-muted" style={{fontSize: '0.75rem'}}>
           Enter value manually. Validation will apply on submit.
        </Form.Text> */}
            </div>
        );
    }

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <InputGroup hasValidation>
                <Form.Control
                    type="text"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    isInvalid={isInvalid}
                    required={required}
                    pattern={nativePattern}
                    autoComplete="off"
                    inputMode={numericOnly ? "numeric" : "text"}
                />
                {/* Clear Button (X) - Only show if there is a value */}
                {searchTerm && (
                    <InputGroup.Text
                        style={{ cursor: 'pointer', background: 'white', borderLeft: 'none' }}
                        onClick={handleClear}
                    >
                        ✕
                    </InputGroup.Text>
                )}
                <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>
            </InputGroup>

            {showDropdown && (
                <div className="shadow-sm border rounded bg-white"
                    style={{
                        position: 'absolute',
                        zIndex: 1000,
                        width: '100%',
                        marginTop: '2px'
                    }}
                >
                    {loading && page === 1 ? (
                        <div className="p-2 text-center text-muted"><Spinner animation="border" size="sm" /> Loading...</div>
                    ) : options.length > 0 ? (
                        <>
                            <ListGroup variant="flush">
                                {options.map((item, idx) => (
                                    <ListGroup.Item
                                        key={`${item[valueKey]}-${idx}`}
                                        action
                                        onClick={() => handleSelect(item)}
                                        className="py-2 px-3 border-0"
                                        style={{ fontSize: '0.9rem' }}
                                    >
                                        {item[labelKey]}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>

                            {/* --- Pagination Controls --- */}
                            <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light">
                                <Button
                                    variant="link"
                                    size="sm"
                                    disabled={page === 1}
                                    onClick={handlePrevPage}
                                    className="text-decoration-none p-0"
                                >
                                    &laquo; Prev
                                </Button>
                                <span className="text-muted small">Page {page}</span>
                                <Button
                                    variant="link"
                                    size="sm"
                                    disabled={!hasNext}
                                    onClick={handleNextPage}
                                    className="text-decoration-none p-0"
                                >
                                    Next &raquo;
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="p-3 text-center">
                            <div className="text-muted small mb-2">{searchTerm ? 'No results found' : 'Type to search...'}</div>

                            {/* --- Add New Option (Visible when no results) --- */}
                            {allowNew && (
                                <div className="border-top pt-2">
                                    {/* {isNewItemValid() ? ( */}
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="w-100"
                                        onClick={switchToManual}
                                    >
                                        + Enter "{searchTerm}" Manually
                                    </Button>
                                    {/* //  ) : (
                                    //     <div className="text-danger small">
                                    //         {newItemError}
                                    //     </div>
                                    // )} */}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;